import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import LocalModal from '@/components/LocalModal';
import ContatoModal from '@/components/ContatoModal';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

type ClienteFormData = {
  codigo_cliente: string;
  razao_social: string;
  cnpj: string;
  apelido: string;
  inscricao_estadual: string;
  inscricao_municipal: string;
  email_nfe: string;
  fora_municipio: boolean;
  ibge_municipio: string;
  limite_credito: number;
  matricula_cei_cno: string;
  agencia: string;
  conta: string;
  pix: string;
  banco: string;
  cnpj_bancario: string;
};

interface ClienteFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  clienteToEdit?: any;
}

export function ClienteFormModal({ open, onOpenChange, onSuccess, clienteToEdit }: ClienteFormModalProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [locais, setLocais] = useState([]);
  const [contatos, setContatos] = useState([]);
  const [showLocalModal, setShowLocalModal] = useState(false);
  
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ClienteFormData>();

  const foraDoMunicipio = watch('fora_municipio');

  useEffect(() => {
    if (open) {
      if (clienteToEdit) {
        // Populate form with existing client data
        setValue('codigo_cliente', clienteToEdit.codigo_cliente);
        setValue('razao_social', clienteToEdit.nome);
        setValue('cnpj', clienteToEdit.documento || '');
        setValue('apelido', clienteToEdit.apelido || '');
        setValue('inscricao_estadual', clienteToEdit.inscricao_estadual || '');
        setValue('inscricao_municipal', clienteToEdit.inscricao_municipal || '');
        setValue('email_nfe', clienteToEdit.email_nfe || '');
        setValue('fora_municipio', clienteToEdit.fora_municipio || false);
        setValue('ibge_municipio', clienteToEdit.ibge_municipio || '');
        setValue('limite_credito', clienteToEdit.limite_credito || 0);
        setValue('matricula_cei_cno', clienteToEdit.matricula_cei_cno || '');
        setValue('agencia', clienteToEdit.agencia || '');
        setValue('conta', clienteToEdit.conta || '');
        setValue('pix', clienteToEdit.pix || '');
        setValue('banco', clienteToEdit.banco || '');
        setValue('cnpj_bancario', clienteToEdit.cnpj_bancario || '');
      } else {
        // Generate next client code for new clients
        generateNextClientCode();
      }
      loadLocais();
      loadContatos();
    }
  }, [open, clienteToEdit]);

  const generateNextClientCode = async () => {
    try {
      const { data, error } = await supabase
        .from('clientes')
        .select('codigo_cliente')
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) throw error;

      let nextNumber = 1;
      if (data && data.length > 0 && data[0].codigo_cliente) {
        const lastCode = data[0].codigo_cliente;
        const numberPart = lastCode.replace('CLI', '');
        nextNumber = parseInt(numberPart) + 1;
      }

      const newCode = `CLI${nextNumber.toString().padStart(4, '0')}`;
      setValue('codigo_cliente', newCode);
    } catch (error) {
      console.error('Erro ao gerar código do cliente:', error);
    }
  };

  const loadLocais = async () => {
    try {
      const { data, error } = await supabase
        .from('locais')
        .select('*')
        .is('cliente_id', null);

      if (error) throw error;
      setLocais(data || []);
    } catch (error) {
      console.error('Erro ao carregar locais:', error);
    }
  };

  const loadContatos = async () => {
    try {
      const { data, error } = await supabase
        .from('contatos')
        .select('*')
        .is('cliente_id', null);

      if (error) throw error;
      setContatos(data || []);
    } catch (error) {
      console.error('Erro ao carregar contatos:', error);
    }
  };

  const onSubmit = async (data: ClienteFormData) => {
    setLoading(true);
    try {
      if (clienteToEdit) {
        // Update existing client
        const { error } = await supabase
          .from('clientes')
          .update({
            codigo_cliente: data.codigo_cliente,
            nome: data.razao_social,
            documento: data.cnpj,
            apelido: data.apelido,
            inscricao_estadual: data.inscricao_estadual,
            inscricao_municipal: data.inscricao_municipal,
            email_nfe: data.email_nfe,
            fora_municipio: data.fora_municipio,
            ibge_municipio: data.ibge_municipio,
            limite_credito: data.limite_credito,
            matricula_cei_cno: data.matricula_cei_cno,
            agencia: data.agencia,
            conta: data.conta,
            pix: data.pix,
            banco: data.banco,
            cnpj_bancario: data.cnpj_bancario,
          })
          .eq('id', clienteToEdit.id);

        if (error) throw error;

        toast({
          title: "Sucesso",
          description: "Cliente atualizado com sucesso!",
        });
      } else {
        // Create new client
        const { data: tipoCliente } = await supabase
          .from('tipo_clientes')
          .select('id')
          .limit(1)
          .single();

        const { error } = await supabase
          .from('clientes')
          .insert({
            codigo_cliente: data.codigo_cliente,
            nome: data.razao_social,
            documento: data.cnpj,
            apelido: data.apelido,
            inscricao_estadual: data.inscricao_estadual,
            inscricao_municipal: data.inscricao_municipal,
            email_nfe: data.email_nfe,
            fora_municipio: data.fora_municipio,
            ibge_municipio: data.ibge_municipio,
            limite_credito: data.limite_credito,
            matricula_cei_cno: data.matricula_cei_cno,
            agencia: data.agencia,
            conta: data.conta,
            pix: data.pix,
            banco: data.banco,
            cnpj_bancario: data.cnpj_bancario,
            tipo_cliente_id: tipoCliente?.id || '00000000-0000-0000-0000-000000000000',
          });

        if (error) throw error;

        toast({
          title: "Sucesso",
          description: "Cliente cadastrado com sucesso!",
        });
      }

      reset();
      onSuccess();
      onOpenChange(false);
    } catch (error: any) {
      console.error('Erro ao processar cliente:', error);
      toast({
        title: "Erro",
        description: "Erro ao processar cliente: " + error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{clienteToEdit ? 'Editar Cliente' : 'Novo Cliente'}</DialogTitle>
          <DialogDescription>
            {clienteToEdit ? 'Atualize as informações do cliente.' : 'Cadastre um novo cliente preenchendo as informações abaixo.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Tabs defaultValue="dados-basicos" className="w-full">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="dados-basicos">Dados Básicos</TabsTrigger>
              <TabsTrigger value="locais">Locais</TabsTrigger>
              <TabsTrigger value="fiscal">Fiscal</TabsTrigger>
              <TabsTrigger value="contatos">Contatos</TabsTrigger>
              <TabsTrigger value="financeiro">Financeiro</TabsTrigger>
            </TabsList>

            <TabsContent value="dados-basicos" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Informações Básicas</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="codigo_cliente">Código Cliente</Label>
                      <Input
                        id="codigo_cliente"
                        {...register('codigo_cliente', { required: 'Código é obrigatório' })}
                        readOnly
                        className="bg-muted"
                      />
                      {errors.codigo_cliente && (
                        <p className="text-sm text-destructive">{errors.codigo_cliente.message}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="cnpj">CNPJ</Label>
                      <Input
                        id="cnpj"
                        {...register('cnpj', { required: 'CNPJ é obrigatório' })}
                      />
                      {errors.cnpj && (
                        <p className="text-sm text-destructive">{errors.cnpj.message}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="razao_social">Razão Social</Label>
                      <Input
                        id="razao_social"
                        {...register('razao_social', { required: 'Razão Social é obrigatória' })}
                      />
                      {errors.razao_social && (
                        <p className="text-sm text-destructive">{errors.razao_social.message}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="apelido">Apelido</Label>
                      <Input
                        id="apelido"
                        {...register('apelido')}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="locais" className="space-y-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle>Locais Vinculados</CardTitle>
                  <LocalModal
                    onLocalCriado={loadLocais}
                    trigger={
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        Novo Local
                      </Button>
                    }
                  />
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Os locais serão vinculados ao cliente após a criação.
                  </p>
                  {locais.length === 0 ? (
                    <p className="text-center text-muted-foreground py-4">
                      Nenhum local disponível para vinculação.
                    </p>
                  ) : (
                    <div className="space-y-2 mt-4">
                      {locais.map((local: any) => (
                        <div key={local.id} className="p-3 border rounded-lg">
                          <div className="font-medium">{local.nome}</div>
                          <div className="text-sm text-muted-foreground">
                            {local.cidade} - {local.estado}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="fiscal" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Informações Fiscais</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="inscricao_estadual">Inscrição Estadual (IE)</Label>
                      <Input
                        id="inscricao_estadual"
                        {...register('inscricao_estadual')}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="inscricao_municipal">Inscrição Municipal (IM)</Label>
                      <Input
                        id="inscricao_municipal"
                        {...register('inscricao_municipal')}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email_nfe">E-mail NFE</Label>
                      <Input
                        id="email_nfe"
                        type="email"
                        {...register('email_nfe')}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="ibge_municipio">IBGE Município</Label>
                      <Input
                        id="ibge_municipio"
                        {...register('ibge_municipio')}
                      />
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="fora_municipio"
                      {...register('fora_municipio')}
                    />
                    <Label htmlFor="fora_municipio">Fora do Município?</Label>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="contatos" className="space-y-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle>Contatos Associados</CardTitle>
                  <ContatoModal
                    onContatoCriado={loadContatos}
                    trigger={
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        Novo Contato
                      </Button>
                    }
                  />
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Os contatos serão vinculados ao cliente após a criação.
                  </p>
                  {contatos.length === 0 ? (
                    <p className="text-center text-muted-foreground py-4">
                      Nenhum contato disponível para vinculação.
                    </p>
                  ) : (
                    <div className="space-y-2 mt-4">
                      {contatos.map((contato: any) => (
                        <div key={contato.id} className="p-3 border rounded-lg">
                          <div className="font-medium">{contato.nome}</div>
                          <div className="text-sm text-muted-foreground">
                            {contato.email} - {contato.telefone}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="financeiro" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Informações Financeiras</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="limite_credito">Limite de Crédito</Label>
                      <Input
                        id="limite_credito"
                        type="number"
                        step="0.01"
                        {...register('limite_credito', { valueAsNumber: true })}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="matricula_cei_cno">Matrícula CEI/CNO</Label>
                      <Input
                        id="matricula_cei_cno"
                        {...register('matricula_cei_cno')}
                      />
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <h4 className="font-medium mb-4">Dados Bancários de Recebimento</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="banco">Banco</Label>
                        <Input
                          id="banco"
                          {...register('banco')}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="agencia">Agência</Label>
                        <Input
                          id="agencia"
                          {...register('agencia')}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="conta">Conta</Label>
                        <Input
                          id="conta"
                          {...register('conta')}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="pix">PIX</Label>
                        <Input
                          id="pix"
                          {...register('pix')}
                        />
                      </div>

                      <div className="space-y-2 col-span-2">
                        <Label htmlFor="cnpj_bancario">CNPJ Bancário</Label>
                        <Input
                          id="cnpj_bancario"
                          {...register('cnpj_bancario')}
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          <div className="flex justify-end space-x-2 mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Salvando...' : (clienteToEdit ? 'Atualizar Cliente' : 'Salvar Cliente')}
            </Button>
          </div>
        </form>
      </DialogContent>

    </Dialog>
  );
}